import React, { useState, useEffect, useCallback } from 'react';
import { Key, Save, Check, Sparkles, Clock } from 'lucide-react';
import toast from 'react-hot-toast';
import { apiGet, apiPost } from '../lib/api';
import Modal from './ui/Modal';
import ConfirmDialog from './ui/ConfirmDialog';
import MyApiKeys from './MyApiKeys';

interface ApiKeySettingsProps {
  onClose: () => void;
}

interface GeminiAccessStatus {
  allowed: boolean;
  request: { status: 'pending' | 'approved' | 'denied' } | null;
}

const ApiKeySettings: React.FC<ApiKeySettingsProps> = ({ onClose }) => {
  const [googleKey, setGoogleKey] = useState('');
  const [isGoogleSaved, setIsGoogleSaved] = useState(false);

  const [access, setAccess] = useState<GeminiAccessStatus | null>(null);
  const [requesting, setRequesting] = useState(false);
  const [confirmingClear, setConfirmingClear] = useState(false);

  const loadAccess = useCallback(async () => {
    try {
      setAccess(await apiGet<GeminiAccessStatus>('/gemini-access/status'));
    } catch (err) {
      console.error('Failed to load Gemini access status:', err);
    }
  }, []);

  useEffect(() => {
    const savedGoogleKey = localStorage.getItem('user_google_api_key');
    if (savedGoogleKey) {
      setGoogleKey(savedGoogleKey);
      setIsGoogleSaved(true);
    }
    loadAccess();
  }, [loadAccess]);

  const handleRequestAccess = async () => {
    setRequesting(true);
    try {
      await apiPost('/gemini-access/request');
      toast.success('Access request submitted — TRB will review it.');
      await loadAccess();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to submit request');
    } finally {
      setRequesting(false);
    }
  };

  const handleSaveGoogle = () => {
    if (googleKey.trim()) {
      localStorage.setItem('user_google_api_key', googleKey.trim());
      setIsGoogleSaved(true);
      toast.success('Google API key saved successfully!');
      setTimeout(() => window.location.reload(), 1000);
    } else {
      toast.error('Please enter a valid API key');
    }
  };

  const handleClearGoogle = () => {
    localStorage.removeItem('user_google_api_key');
    setGoogleKey('');
    setIsGoogleSaved(false);
    setConfirmingClear(false);
    toast.success('Google API key removed');
    setTimeout(() => window.location.reload(), 1000);
  };

  const requestStatus = access?.request?.status;

  return (
    <Modal
      onClose={onClose}
      title="API Settings"
      icon={<Key className="h-5 w-5 text-indigo-600 dark:text-indigo-400" />}
    >
      <div className="space-y-6">
          {/* Shared Gemini access */}
          <div className="bg-gradient-to-br from-purple-50 to-indigo-50 dark:from-purple-900/30 dark:to-indigo-900/30 border border-purple-200 dark:border-purple-700 rounded-lg p-4">
            <h3 className="font-semibold text-purple-900 dark:text-purple-200 mb-2 flex items-center gap-2">
              <Sparkles size={18} /> Shared Gemini Access
            </h3>
            {access?.allowed ? (
              <p className="text-sm text-green-700 dark:text-green-300 flex items-center gap-2">
                <Check size={16} /> You have access to the shared Gemini key — no API key needed.
              </p>
            ) : requestStatus === 'pending' ? (
              <p className="text-sm text-amber-700 dark:text-amber-300 flex items-center gap-2">
                <Clock size={16} /> Your access request is pending review by TRB.
              </p>
            ) : (
              <div className="space-y-2">
                <p className="text-sm text-purple-800 dark:text-purple-300">
                  {requestStatus === 'denied'
                    ? 'Your previous request was declined. You can request again, or add your own key below.'
                    : 'Request access to use Gemini features without your own API key, or add your own key below.'}
                </p>
                <button
                  type="button"
                  onClick={handleRequestAccess}
                  disabled={requesting}
                  className="px-4 py-2 bg-purple-600 hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg text-sm font-medium transition-colors"
                >
                  {requesting ? 'Submitting…' : 'Request Gemini access'}
                </button>
              </div>
            )}
          </div>

          {/* Own Gemini key (optional alternative) */}
          <div className="space-y-4">
            <div>
              <label
                htmlFor="google-api-key"
                className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
              >
                Or use your own Google / Gemini API Key
              </label>
              <input
                id="google-api-key"
                type="password"
                value={googleKey}
                onChange={(e) => {
                  setGoogleKey(e.target.value);
                  setIsGoogleSaved(false);
                }}
                placeholder="AIza..."
                className="w-full px-4 py-2 border border-gray-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 bg-white dark:bg-slate-700 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500"
              />
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                Get a key from{' '}
                <a
                  href="https://aistudio.google.com/app/apikey"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-indigo-600 dark:text-indigo-400 hover:text-indigo-700 dark:hover:text-indigo-300 underline"
                >
                  Google AI Studio
                </a>
                . Stored in your browser only.
              </p>
            </div>

            <div className="flex space-x-3">
              <button
                type="button"
                onClick={handleSaveGoogle}
                disabled={!googleKey.trim() || isGoogleSaved}
                className="flex-1 bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-lg
                         flex items-center justify-center space-x-2 disabled:bg-gray-300 dark:disabled:bg-slate-600
                         disabled:cursor-not-allowed transition-colors"
              >
                {isGoogleSaved ? (
                  <>
                    <Check size={20} />
                    <span>Saved</span>
                  </>
                ) : (
                  <>
                    <Save size={20} />
                    <span>Save Key</span>
                  </>
                )}
              </button>

              {isGoogleSaved && (
                <button
                  type="button"
                  onClick={() => setConfirmingClear(true)}
                  className="px-4 py-2 border border-red-300 dark:border-red-700 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30
                           rounded-lg transition-colors"
                >
                  Clear Key
                </button>
              )}
            </div>
          </div>

          {/* Keys for the public Hinglish Subs API */}
          <div className="border-t border-gray-200 dark:border-slate-700 pt-6">
            <MyApiKeys />
          </div>
      </div>

      <ConfirmDialog
        open={confirmingClear}
        title="Remove API key?"
        description="This removes your saved Google API key from this browser. The page will reload afterwards."
        confirmLabel="Remove key"
        onConfirm={handleClearGoogle}
        onCancel={() => setConfirmingClear(false)}
      />
    </Modal>
  );
};

export default ApiKeySettings;
